<template>
  <div class="px-6 py-6 space-y-6 font-light select-none text-left min-h-screen transition-colors duration-200"
    :class="isDark ? 'bg-zinc-950 text-zinc-400' : 'bg-zinc-50 text-zinc-600'">

    <div v-if="isLoading" class="flex justify-center items-center min-h-screen">
      <div class="text-sm transition-colors duration-200"
        :class="isDark ? 'text-zinc-500' : 'text-zinc-400'">
        Chargement des données...
      </div>
    </div>

    <template v-else-if="client">
      <div class="flex sm:flex-row justify-between items-start sm:items-center border-b pb-5 gap-4 transition-colors duration-200"
        :class="isDark ? 'border-zinc-900/60' : 'border-zinc-200'">
        <div class="space-y-1">
          <div class="flex items-center gap-2 text-xs font-mono uppercase tracking-widest transition-colors duration-200"
            :class="isDark ? 'text-zinc-500' : 'text-zinc-400'">
            <button @click="goBack" class="transition-colors flex items-center gap-1 cursor-pointer"
              :class="isDark ? 'hover:text-white' : 'hover:text-zinc-900'">
              ← Registre
            </button>
            <span>/</span>
            <span class="text-[#D5CCBC]">Fiche Affilié Privée</span>
          </div>
          <h1 class="text-xl font-medium tracking-tight transition-colors duration-200"
            :class="isDark ? 'text-white' : 'text-zinc-900'">
            {{ client.nom }} {{ client.prenom }}
          </h1>
        </div>

        <div class="flex gap-3 sm:w-auto">
          <button
            @click="toggleRestriction"
            :disabled="isUpdating"
            class="flex-1 sm:flex-none px-4 py-2 rounded-xl text-xs font-medium uppercase tracking-wider border transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            :class="client.profilRisque
              ? 'bg-red-950/20 text-red-400 border-red-900/40 hover:bg-red-950/40'
              : isDark
                ? 'bg-zinc-900 text-zinc-400 border-zinc-800 hover:text-white'
                : 'bg-zinc-50 text-zinc-600 border-zinc-200 hover:bg-zinc-100'"
          >
            {{ client.profilRisque ? 'Lever Restriction Risque' : 'Signaler Profil à Risque' }}
          </button>
          <button
            @click="editProfile"
            class="flex-1 sm:flex-none px-5 py-2.5 rounded-xl text-xs font-semibold uppercase tracking-wider transition-all duration-300 shadow-xl cursor-pointer"
            :class="isDark
              ? 'bg-white hover:bg-[#D5CCBC] text-black shadow-white/5'
              : 'bg-zinc-900 hover:bg-zinc-800 text-white shadow-zinc-900/10'"
          >
            Éditer le profil
          </button>
        </div>
      </div>

      <div class="grid lg:grid-cols-3 gap-6 items-start w-full">

        <div class="space-y-6 lg:col-span-1">

          <div class="border rounded-2xl p-5 space-y-5 transition-colors duration-200"
            :class="isDark ? 'bg-zinc-900/20 border-zinc-900' : 'bg-white border-zinc-200'">
            <h3 class="text-[10px] uppercase tracking-widest font-medium font-mono transition-colors duration-200"
              :class="isDark ? 'text-zinc-500' : 'text-zinc-400'">
              Indicateurs de Fidélité
            </h3>

            <div
              class="p-4 rounded-xl border flex items-center gap-3.5"
              :class="(client.profilRisque || client.tauxAnnulation > 20) ? 'bg-red-950/20 border-red-900/50 text-red-400' : 'bg-emerald-950/10 border-emerald-900/40 text-emerald-400'"
            >
              <span class="relative flex h-2.5 w-2.5 shrink-0">
                <span class="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" :class="(client.profilRisque || client.tauxAnnulation > 20) ? 'bg-red-400' : 'bg-emerald-400'"></span>
                <span class="relative inline-flex rounded-full h-2.5 w-2.5" :class="(client.profilRisque || client.tauxAnnulation > 20) ? 'bg-red-500' : 'bg-emerald-500'"></span>
              </span>
              <div class="text-left space-y-0.5">
                <p class="text-xs font-semibold uppercase tracking-wide">
                  {{ (client.profilRisque || client.tauxAnnulation > 20) ? 'Alerte : Profil à Risque' : 'Indice Confiance Maximal' }}
                </p>
                <p class="text-[11px] text-zinc-500 font-light">
                  {{ (client.profilRisque || client.tauxAnnulation > 20) ? `Taux d'annulation élevé (${client.tauxAnnulation}%)` : 'Aucun incident ou défaut d\'acompte répertorié.' }}
                </p>
              </div>
            </div>

            <div class="grid grid-cols-2 gap-3">
              <div class="p-3.5 border rounded-xl text-left space-y-1 transition-colors duration-200"
                :class="isDark ? 'bg-zinc-950 border-zinc-900' : 'bg-zinc-50 border-zinc-200'">
                <p class="text-[10px] uppercase tracking-wider font-mono transition-colors duration-200"
                  :class="isDark ? 'text-zinc-500' : 'text-zinc-400'">
                  Sessions Totales
                </p>
                <p class="text-xl font-mono font-medium transition-colors duration-200"
                  :class="isDark ? 'text-white' : 'text-zinc-900'">
                  {{ client.nbRdv }}
                </p>
              </div>
              <div class="p-3.5 border rounded-xl text-left space-y-1 transition-colors duration-200"
                :class="[
                  client.tauxAnnulation > 20 ? 'border-red-900/40' : '',
                  isDark ? 'bg-zinc-950 border-zinc-900' : 'bg-zinc-50 border-zinc-200'
                ]">
                <p class="text-[10px] uppercase tracking-wider font-mono transition-colors duration-200"
                  :class="isDark ? 'text-zinc-500' : 'text-zinc-400'">
                  Taux Annulation
                </p>
                <p class="text-xl font-mono font-medium"
                  :class="client.tauxAnnulation > 20 ? 'text-red-400' : (isDark ? 'text-zinc-300' : 'text-zinc-700')">
                  {{ client.tauxAnnulation }}%
                </p>
              </div>
              <div class="p-3.5 border rounded-xl text-left space-y-1 md:col-span-2 transition-colors duration-200"
                :class="isDark ? 'bg-zinc-950 border-zinc-900' : 'bg-zinc-50 border-zinc-200'">
                <p class="text-[10px] uppercase tracking-wider font-mono transition-colors duration-200"
                  :class="isDark ? 'text-zinc-500' : 'text-zinc-400'">
                  Volume d'affaires injecté
                </p>
                <p class="text-lg font-mono font-medium text-[#D5CCBC]">{{ client.totalCa }} €</p>
              </div>
            </div>

            <div class="pt-4 border-t space-y-2.5 text-xs transition-colors duration-200"
              :class="isDark ? 'border-zinc-900' : 'border-zinc-200'">
              <div class="flex justify-between pb-1">
                <span class="transition-colors duration-200"
                  :class="isDark ? 'text-zinc-500' : 'text-zinc-400'">
                  Adresse Email
                </span>
                <span class="font-mono transition-colors duration-200"
                  :class="isDark ? 'text-zinc-300' : 'text-zinc-700'">
                  {{ client.email }}
                </span>
              </div>
              <div class="flex justify-between pb-1">
                <span class="transition-colors duration-200"
                  :class="isDark ? 'text-zinc-500' : 'text-zinc-400'">
                  Mobile
                </span>
                <span class="font-mono transition-colors duration-200"
                  :class="isDark ? 'text-zinc-300' : 'text-zinc-700'">
                  {{ client.telephone }}
                </span>
              </div>
              <div class="flex justify-between">
                <span class="transition-colors duration-200"
                  :class="isDark ? 'text-zinc-500' : 'text-zinc-400'">
                  Secteur Local
                </span>
                <span class="transition-colors duration-200"
                  :class="isDark ? 'text-zinc-300' : 'text-zinc-700'">
                  {{ client.ville || 'Non renseigné' }}
                </span>
              </div>
            </div>
          </div>

          <div class="border rounded-2xl p-5 space-y-4 transition-colors duration-200"
            :class="isDark ? 'bg-zinc-900/20 border-zinc-900' : 'bg-white border-zinc-200'">
            <div class="flex justify-between items-center">
              <h3 class="text-[10px] uppercase tracking-widest font-medium font-mono transition-colors duration-200"
                :class="isDark ? 'text-zinc-500' : 'text-zinc-400'">Dernières Directives Techniques</h3>
              <button
                v-if="!isEditingNotes"
                @click="startEditingNotes"
                class="text-xs px-3 py-1 rounded-lg border transition-colors duration-200"
                :class="isDark
                  ? 'bg-zinc-900 border-zinc-800 text-zinc-400 hover:text-white hover:bg-zinc-800'
                  : 'bg-zinc-50 border-zinc-200 text-zinc-600 hover:bg-zinc-100'"
              >
                Modifier
              </button>
            </div>

            <div v-if="!isEditingNotes" class="p-4 border rounded-xl transition-colors duration-200"
              :class="isDark ? 'bg-zinc-950 border-zinc-900' : 'bg-zinc-50 border-zinc-200'">
              <p class="text-xs italic leading-relaxed transition-colors duration-200"
                :class="isDark ? 'text-zinc-400' : 'text-zinc-600'">
                {{ client.dernieresNotes ? `"${client.dernieresNotes}"` : "Aucune consigne technique rédigée sur ce profil." }}
              </p>
              <p class="text-[10px] font-mono mt-3 text-right transition-colors duration-200"
                :class="isDark ? 'text-zinc-600' : 'text-zinc-400'">
                Mise à jour : {{ formatDate(client.dateDerniereNote) }}
              </p>
            </div>

            <div v-else class="space-y-3">
              <textarea
                v-model="editedNotes"
                rows="4"
                placeholder="Saisir des directives techniques..."
                class="w-full px-4 py-3 rounded-xl border text-xs font-light focus:outline-none transition-all resize-none"
                :class="isDark
                  ? 'bg-zinc-950 border-zinc-800 text-white focus:border-zinc-700'
                  : 'bg-white border-zinc-200 text-zinc-900 focus:border-zinc-300'"
              ></textarea>
              <div class="flex gap-2">
                <button
                  @click="saveNotes"
                  :disabled="isSavingNotes"
                  class="flex-1 px-4 py-2 rounded-xl text-xs font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  :class="isDark ? 'bg-white hover:bg-zinc-200 text-black' : 'bg-zinc-900 hover:bg-zinc-800 text-white'"
                >
                  {{ isSavingNotes ? 'Enregistrement...' : 'Enregistrer' }}
                </button>
                <button
                  @click="cancelEditingNotes"
                  :disabled="isSavingNotes"
                  class="px-4 py-2 rounded-xl text-xs font-medium border transition-colors disabled:opacity-50"
                  :class="isDark
                    ? 'bg-zinc-900 border-zinc-800 text-zinc-400 hover:text-white'
                    : 'bg-white border-zinc-200 text-zinc-600 hover:bg-zinc-50'"
                >
                  Annuler
                </button>
              </div>
            </div>
          </div>

        </div>

        <div class="space-y-6 lg:col-span-2">

          <div class="border rounded-2xl p-5 space-y-4 transition-colors duration-200"
            :class="isDark ? 'bg-zinc-900/20 border-zinc-900' : 'bg-white border-zinc-200'">
            <div class="flex justify-between items-center border-b pb-3 transition-colors duration-200"
              :class="isDark ? 'border-zinc-900' : 'border-zinc-200'">
              <h3 class="text-sm font-medium tracking-wide transition-colors duration-200"
                :class="isDark ? 'text-white' : 'text-zinc-900'">
                Registre Historique des Séventions
              </h3>
              <span class="text-[10px] font-mono uppercase px-2 py-0.5 rounded border transition-colors duration-200"
                :class="isDark
                  ? 'bg-zinc-900 border-zinc-800 text-zinc-500'
                  : 'bg-zinc-50 border-zinc-200 text-zinc-600'">
                {{ client.sessionsArchive?.length || 0 }} Entrées
              </span>
            </div>

            <div class="overflow-x-auto no-scrollbar">
              <table class="w-full text-xs text-left">
                <thead class="border-b text-[10px] uppercase tracking-wider font-mono transition-colors duration-200"
                  :class="isDark
                    ? 'text-zinc-600 border-zinc-900/80'
                    : 'text-zinc-500 border-zinc-200'">
                  <tr>
                    <th class="pb-3 font-medium">Date / Créneau</th>
                    <th class="pb-3 font-medium">Actes Réalisés</th>
                    <th class="pb-3 font-medium">Opérateur</th>
                    <th class="pb-3 font-medium">Tarif</th>
                    <th class="pb-3 text-right font-medium">Statut</th>
                  </tr>
                </thead>
                <tbody class="divide-y font-light transition-colors duration-200"
                  :class="isDark ? 'divide-zinc-900/40' : 'divide-zinc-200/60'">
                  <tr
                    v-for="session in client.sessionsArchive"
                    :key="session.id"
                    class="transition-colors"
                    :class="isDark ? 'hover:bg-zinc-900/20' : 'hover:bg-zinc-50'"
                  >
                    <td class="py-3.5 pr-2 whitespace-nowrap">
                      <span class="font-medium transition-colors duration-200"
                        :class="isDark ? 'text-zinc-300' : 'text-zinc-700'">
                        {{ formatDate(session.date) }}
                      </span>
                      <span class="block text-[10px] font-mono mt-0.5 transition-colors duration-200"
                        :class="isDark ? 'text-zinc-600' : 'text-zinc-400'">
                        {{ session.heure }}
                      </span>
                    </td>
                    <td class="py-3.5 pr-2 max-w-[180px] truncate font-medium transition-colors duration-200"
                      :class="isDark ? 'text-zinc-400' : 'text-zinc-600'">
                      {{ session.services.join(', ') }}
                    </td>
                    <td class="py-3.5 pr-2 font-light transition-colors duration-200"
                      :class="isDark ? 'text-zinc-500' : 'text-zinc-500'">
                      {{ session.employe }}
                    </td>
                    <td class="py-3.5 pr-2 font-mono font-medium transition-colors duration-200"
                      :class="isDark ? 'text-zinc-300' : 'text-zinc-700'">
                      {{ session.montant }} €
                    </td>
                    <td class="py-3.5 text-right whitespace-nowrap">
                      <span
                        class="px-2 py-0.5 rounded-md text-[9px] font-semibold uppercase tracking-wider font-mono border"
                        :class="[
                          session.status === 'Honoré' ? 'bg-emerald-950/40 text-emerald-400 border-emerald-900/40' :
                          session.status === 'Absent' ? 'bg-red-950/40 text-red-400 border-red-900/40' :
                          (isDark ? 'bg-zinc-900 text-zinc-500 border-zinc-800' : 'bg-zinc-50 text-zinc-600 border-zinc-200')
                        ]"
                      >
                        {{ session.status }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div v-if="!client.sessionsArchive || client.sessionsArchive.length === 0" class="text-center py-10">
              <p class="text-xs italic transition-colors duration-200"
                :class="isDark ? 'text-zinc-600' : 'text-zinc-400'">
                Aucune transaction ou session archivée sur ce profil.
              </p>
            </div>

          </div>

        </div>

      </div>
    </template>

    <div v-else class="flex justify-center items-center min-h-screen">
      <div class="text-sm transition-colors duration-200"
        :class="isDark ? 'text-zinc-500' : 'text-zinc-400'">
        Client non trouvé
      </div>
    </div>

  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { toast } from 'vue3-toastify'
import 'vue3-toastify/dist/index.css'
import api from '@/api'
import { useTheme } from '@/composables/useTheme'

const { isDark } = useTheme()
const route = useRoute()
const router = useRouter()
const isLoading = ref(true)
const isUpdating = ref(false)
const client = ref(null)
const isEditingNotes = ref(false)
const isSavingNotes = ref(false)
const editedNotes = ref('')

// Fetch client details on mount
onMounted(async () => {
  await fetchClientDetails()
})

const fetchClientDetails = async () => {
  try {
    isLoading.value = true
    const clientId = route.params.id

    if (!clientId) {
      toast.error('ID client manquant', {
        position: 'top-right',
        autoClose: 3000
      })
      router.push('/clients')
      return
    }

    const response = await api.clients.getClientById(clientId)
    client.value = response.data

  } catch (error) {
    console.error('Erreur lors de la récupération du client:', error)
    toast.error(
      error.response?.data?.message || 'Erreur lors de la récupération des données du client',
      {
        position: 'top-right',
        autoClose: 3000
      }
    )
    router.push('/clients')
  } finally {
    isLoading.value = false
  }
}

// Toggle risk profile (blacklist status)
const toggleRestriction = async () => {
  if (isUpdating.value) return

  try {
    isUpdating.value = true
    const newStatus = !client.value.profilRisque

    await api.clients.updateClient(client.value.id, {
      blacklist: newStatus,
      acompte_demande: client.value.acompte_demande
    })

    client.value.profilRisque = newStatus
    client.value.blacklist = newStatus

    toast.success(
      newStatus
        ? 'Client marqué comme profil à risque'
        : 'Restriction de risque levée',
      {
        position: 'top-right',
        autoClose: 2000
      }
    )

  } catch (error) {
    console.error('Erreur lors de la mise à jour du profil de risque:', error)
    toast.error(
      error.response?.data?.message || 'Erreur lors de la mise à jour',
      {
        position: 'top-right',
        autoClose: 3000
      }
    )
  } finally {
    isUpdating.value = false
  }
}

// Navigate to edit profile
const editProfile = () => {
  // Redirect to client creation page with edit mode and client ID
  router.push({
    path: '/clients/new',
    query: { edit: client.value.id }
  })
}

// Navigate back to clients list
const goBack = () => {
  router.push('/clients')
}

const formatDate = (dateStr) => {
  if (!dateStr) return 'Inconnue'
  return new Date(dateStr).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  })
}

// Start editing notes
const startEditingNotes = () => {
  editedNotes.value = client.value.dernieresNotes || ''
  isEditingNotes.value = true
}

// Cancel editing notes
const cancelEditingNotes = () => {
  isEditingNotes.value = false
  editedNotes.value = ''
}

// Save notes
const saveNotes = async () => {
  if (isSavingNotes.value) return

  try {
    isSavingNotes.value = true

    await api.clients.updateClient(client.value.id, {
      notes: editedNotes.value,
      blacklist: client.value.blacklist || client.value.profilRisque || false,
      acompte_demande: client.value.acompte_demande || false
    })

    client.value.dernieresNotes = editedNotes.value
    client.value.dateDerniereNote = new Date().toISOString()

    toast.success('Notes mises à jour avec succès', {
      position: 'top-right',
      autoClose: 2000
    })

    isEditingNotes.value = false

  } catch (error) {
    console.error('Erreur lors de la mise à jour des notes:', error)
    toast.error(
      error.response?.data?.message || 'Erreur lors de la mise à jour des notes',
      {
        position: 'top-right',
        autoClose: 3000
      }
    )
  } finally {
    isSavingNotes.value = false
  }
}
</script>

<style scoped>
/* Core micro system resets */
.no-scrollbar::-webkit-scrollbar {
  display: none;
}
.no-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
</style>
